import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, TrendingUp, Wallet, PiggyBank, Calendar, Lock, Crown, ArrowRightLeft } from 'lucide-react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid
} from 'recharts';
import { useSpaceStore } from '../stores/spaceStore';
import { useExpenseStore } from '../stores/expenseStore';
import { useAuthStore } from '../stores/authStore';
import { supabase } from '../lib/supabase';
import Card from '../components/ui/Card';
import Badge from '../components/ui/Badge';
import Skeleton, { SkeletonList } from '../components/ui/Skeleton';
import EmptyState from '../components/ui/EmptyState';
import ProUpgradeModal from '../components/ProUpgradeModal';
import ExpenseEditSheet from '../components/ExpenseEditSheet';
import type { RecentActivityItem } from '../types/activity';

interface FixedResponsibility {
  category_id: string;
  name: string;
  emoji: string;
  amount_cents: number;
  due_day: number;
  autopay: boolean;
  is_paused: boolean;
  paid_by?: string;
}

interface MonthlyTrend {
  month: string;
  label: string;
  total_cents: number;
}

interface PersonalStats {
  member_id: string;
  total_spent_cents: number;
  my_keep_cents: number;
  my_split_share_cents: number;
  my_shared_balance_cents: number;
  category_breakdown: Record<string, number>;
  monthly_trend: MonthlyTrend[];
  fixed_responsibilities: FixedResponsibility[];
  recent_activity: RecentActivityItem[];
}

export default function PersonalDashboard() {
  const navigate = useNavigate();
  const { currentSpace } = useSpaceStore();
  const { isPro } = useAuthStore();
  const { expenses, settlements, subscribeToChanges } = useExpenseStore();
  const [stats, setStats] = useState<PersonalStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [editingExpense, setEditingExpense] = useState<any | null>(null);

  useEffect(() => {
    const fetchStats = async () => {
      if (!currentSpace) return;

      try {
        const { data: { session } } = await supabase.auth.getSession();
        const res = await fetch(`/api/dashboard/personal/${currentSpace.id}`, {
          headers: { 'Authorization': `Bearer ${session?.access_token}` }
        });

        if (res.ok) {
          const data = await res.json();
          setStats(data);
        }
      } catch (err) {
        console.error('Failed to fetch stats:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchStats();
  }, [currentSpace, expenses, settlements]);

  useEffect(() => {
    if (!currentSpace?.id) {
      return undefined;
    }

    const unsubscribe = subscribeToChanges(currentSpace.id);
    return () => unsubscribe();
  }, [currentSpace?.id]);

  const formatCurrency = (cents: number) => `${cents < 0 ? '-' : ''}$${(Math.abs(cents) / 100).toFixed(2)}`;

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const chartColors = {
    primary: 'rgb(var(--color-primary))',
    foreground: 'rgb(var(--color-foreground) / 0.88)',
    tooltipText: 'rgb(var(--color-foreground))',
    border: 'rgb(var(--color-border) / 0.9)',
    grid: 'rgb(var(--color-border) / 0.65)',
    card: 'rgb(var(--color-card))',
  };

  // Category breakdown sorted for progress bars
  const sortedCategories = useMemo(() => {
    if (!stats?.category_breakdown) return [];
    return Object.entries(stats.category_breakdown)
      .filter(([, amount]) => amount > 0)
      .sort(([, a], [, b]) => b - a);
  }, [stats?.category_breakdown]);

  const maxCategorySpend = useMemo(
    () => Math.max(...sortedCategories.map(([, v]) => v), 1),
    [sortedCategories]
  );

  // Chart data formatted for Recharts
  const chartData = useMemo(
    () => (stats?.monthly_trend || []).map(m => ({
      name: m.label,
      amount: m.total_cents / 100,
    })),
    [stats?.monthly_trend]
  );

  // Active fixed responsibilities
  const activeFixed = useMemo(
    () => (stats?.fixed_responsibilities || []).filter(f => !f.is_paused),
    [stats?.fixed_responsibilities]
  );
  const mySharedBalance = stats?.my_shared_balance_cents ?? stats?.my_split_share_cents ?? 0;

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton width="100%" height={100} className="rounded-2xl" />
        <div className="grid grid-cols-2 gap-3">
          <Skeleton width="100%" height={80} />
          <Skeleton width="100%" height={80} />
        </div>
        <Skeleton width="60%" height={24} className="rounded" />
        <SkeletonList count={3} />
      </div>
    );
  }

  if (!currentSpace) {
    return (
      <EmptyState
        icon="📊"
        title="No Data Yet"
        description="Create a space and log some expenses to see your personal dashboard"
        action={{ label: 'Get Started', onClick: () => navigate('/onboarding/space') }}
      />
    );
  }

  return (
    <div className="space-y-6 pb-8">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/')}
          className="p-2 -ml-2 rounded-full hover:bg-muted/10 touch-target"
        >
          <ArrowLeft size={24} />
        </button>
        <h1 className="text-xl font-bold">Personal Dashboard</h1>
      </div>

      {/* Total Spending Card */}
      <Card className="bg-gradient-to-br from-primary/20 to-primary/5 border-primary/20">
        <div className="flex items-center gap-3 mb-1">
          <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
            <Wallet className="w-5 h-5 text-primary" />
          </div>
          <div>
            <p className="text-muted text-sm">Total Spending</p>
            <p className="text-2xl font-bold tabular-nums">
              {formatCurrency(stats?.total_spent_cents || 0)}
            </p>
          </div>
        </div>
      </Card>

      {/* Stats Grid: Keep vs Split */}
      <div className="grid grid-cols-2 gap-3">
        <Card>
          <div className="flex items-center gap-2 mb-2">
            <PiggyBank className="w-4 h-4 text-keep" />
            <span className="text-muted text-xs">Keep Expenses</span>
          </div>
          <p className="text-lg font-bold tabular-nums text-keep">
            {formatCurrency(stats?.my_keep_cents || 0)}
          </p>
        </Card>
        <Card>
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="w-4 h-4 text-primary" />
            <span className="text-muted text-xs">Shared Balance</span>
          </div>
          <p className={`text-lg font-bold tabular-nums ${
            mySharedBalance > 0 ? 'text-primary' : mySharedBalance < 0 ? 'text-danger' : 'text-muted'
          }`}>
            {mySharedBalance > 0 ? '+' : ''}
            {formatCurrency(mySharedBalance)}
          </p>
          <p className="text-[10px] text-muted mt-0.5">
            {mySharedBalance > 0
              ? 'Others owe you'
              : mySharedBalance < 0
                ? 'You owe'
                : 'All settled up'}
          </p>
        </Card>
      </div>

      {/* Fixed Expense Responsibilities */}
      {activeFixed.length > 0 && (
        <div className="space-y-3">
          <h2 className="font-semibold flex items-center gap-2">
            <Calendar className="w-4 h-4 text-fixed" />
            Fixed Responsibilities
          </h2>
          <div className="space-y-2">
            {activeFixed.map(fixed => {
              const today = new Date().getDate();
              const due = fixed.due_day;
              const daysUntil = due >= today ? due - today : 31 - today + due;
              const isDueSoon = daysUntil <= 3;

              return (
                <Card key={fixed.category_id} className={isDueSoon ? 'border-danger/40' : ''}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-xl">{fixed.emoji}</span>
                      <div>
                        <p className="font-medium text-sm">{fixed.name}</p>
                        <p className="text-xs text-muted">
                          Due day {fixed.due_day}
                          {isDueSoon && <span className="text-danger ml-1">· {daysUntil === 0 ? 'Today!' : `${daysUntil}d`}</span>}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold tabular-nums text-sm">
                        {formatCurrency(fixed.amount_cents)}
                      </p>
                      <Badge variant={fixed.autopay ? 'split' : 'default'} size="sm">
                        {fixed.autopay ? 'AUTO' : 'MANUAL'}
                      </Badge>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* Category Breakdown with Progress Bars */}
      {sortedCategories.length > 0 && (
        <div className="space-y-3">
          <h2 className="font-semibold">By Category</h2>
          <div className="space-y-2">
            {sortedCategories.map(([category, amount]) => {
              const pct = Math.round((amount / maxCategorySpend) * 100);
              return (
                <Card key={category} className="py-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm">{category}</span>
                    <span className="font-semibold tabular-nums text-sm">
                      {formatCurrency(amount)}
                    </span>
                  </div>
                  <div className="w-full h-1.5 bg-muted/20 rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${pct}%` }}
                      transition={{ duration: 0.5, ease: 'easeOut' }}
                      className="h-full bg-primary rounded-full"
                    />
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* Spending Trend Chart — Pro Gated */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold flex items-center gap-2">
            <TrendingUp className="w-4 h-4" />
            Spending Trend
          </h2>
          {!isPro && (
            <button
              onClick={() => setShowUpgradeModal(true)}
              className="flex items-center gap-1 text-xs text-yellow-400 font-medium"
            >
              <Crown size={12} /> PRO
            </button>
          )}
        </div>

        {isPro ? (
          <Card className="py-3">
            {chartData.length > 0 && chartData.some(d => d.amount > 0) ? (
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={chartData} barCategoryGap="28%">
                  <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 11, fill: chartColors.foreground }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: chartColors.foreground }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={(v: number) => `$${v}`}
                    width={50}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: chartColors.card,
                      border: `1px solid ${chartColors.border}`,
                      borderRadius: '12px',
                      fontSize: 13,
                      color: chartColors.tooltipText,
                    }}
                    labelStyle={{ color: chartColors.tooltipText, fontWeight: 600 }}
                    itemStyle={{ color: chartColors.tooltipText }}
                    cursor={{ fill: 'rgb(var(--color-primary) / 0.10)' }}
                    formatter={(value: number) => [`$${value.toFixed(2)}`, 'Spent']}
                  />
                  <Bar
                    dataKey="amount"
                    fill={chartColors.primary}
                    radius={[10, 10, 0, 0]}
                    maxBarSize={38}
                  />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[180px] text-muted text-sm">
                Not enough data yet
              </div>
            )}
          </Card>
        ) : (
          <Card
            interactive
            onClick={() => setShowUpgradeModal(true)}
            className="relative overflow-hidden"
          >
            <div className="h-[180px] flex flex-col items-center justify-center gap-3">
              <div className="absolute inset-0 bg-gradient-to-t from-card via-card/80 to-transparent z-10" />
              {/* Fake chart placeholder */}
              <div className="absolute inset-0 flex items-end justify-around px-6 pb-8 opacity-20">
                {[40, 55, 35, 70, 50, 65].map((h, i) => (
                  <div key={i} className="w-6 bg-primary rounded-t" style={{ height: `${h}%` }} />
                ))}
              </div>
              <div className="z-20 text-center">
                <Lock className="w-6 h-6 text-muted mx-auto mb-2" />
                <p className="font-medium text-sm">6-Month Spending Trend</p>
                <p className="text-xs text-muted">Upgrade to Pro to unlock</p>
              </div>
            </div>
          </Card>
        )}
      </div>

      {/* Recent Activity */}
      {stats?.recent_activity && stats.recent_activity.length > 0 && (
        <div className="space-y-3">
          <h2 className="font-semibold">Recent Activity</h2>
          <div className="space-y-2">
            {stats.recent_activity.map((activity, i) => {
              const canEditExpense = activity.type === 'expense' && activity.logged_by === stats?.member_id;
              return (
              <motion.div
                key={`${activity.type}-${activity.id}`}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.03 }}
              >
                <Card
                  className="py-3"
                  interactive={canEditExpense}
                  onClick={canEditExpense ? () => setEditingExpense(activity) : undefined}
                >
                  {activity.type === 'expense' ? (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span>{activity.category?.emoji || '📦'}</span>
                        <div>
                          <span className="text-sm font-medium">{activity.category?.name || 'Uncategorized'}</span>
                          <p className="text-[10px] text-muted">
                            {activity.paid_by_member.display_name} · {formatDate(activity.created_at)}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold tabular-nums text-sm">
                          {formatCurrency(activity.amount_cents)}
                        </p>
                        <Badge
                          variant={activity.is_fixed_autolog ? 'fixed' : activity.action === 'split' ? 'split' : 'keep'}
                          size="sm"
                        >
                          {activity.is_fixed_autolog ? 'FIXED' : activity.action.toUpperCase()}
                        </Badge>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="flex items-center gap-1.5">
                          <span>{activity.from_member_details.avatar_emoji}</span>
                          <ArrowRightLeft size={14} className="text-primary" />
                          <span>{activity.to_member_details.avatar_emoji}</span>
                        </div>
                        <div>
                          <span className="text-sm font-medium">
                            {activity.from_member_details.display_name} paid {activity.to_member_details.display_name}
                          </span>
                          <p className="text-[10px] text-muted">{formatDate(activity.created_at)}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold tabular-nums text-sm text-primary">
                          {formatCurrency(activity.amount_cents)}
                        </p>
                        <Badge variant="split" size="sm">SETTLED</Badge>
                      </div>
                    </div>
                  )}
                </Card>
              </motion.div>
              );
            })}
          </div>
        </div>
      )}

      {/* Empty state if no expenses at all */}
      {(!stats?.recent_activity || stats.recent_activity.length === 0) && !isLoading && (
        <EmptyState
          icon="📊"
          title="No Activity Yet"
          description="Start logging expenses to see your personal breakdown"
          action={{ label: 'Log Expense', onClick: () => navigate('/log') }}
        />
      )}

      <ProUpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        onUpgradeMonthly={() => {}}
        onUpgradeYearly={() => {}}
      />

      <ExpenseEditSheet
        expense={editingExpense}
        isOpen={!!editingExpense}
        onClose={() => setEditingExpense(null)}
      />
    </div>
  );
}
